import type { FocusSession } from "./db";

// Focus timer rules (FC-1, FC-2, D56). Everything is derived from started_at
// and the clock, so a reload, a sleeping phone or a lost connection can
// neither lose nor drift the timer. Pure and tested.

const MIN = 60_000;

/** Longest a single session may be planned or extended to. */
export const MAX_PLANNED_MIN = 480;
/** Extending adds this many minutes. */
export const EXTEND_MIN = 5;
/** A session still "running" this long past its plan was abandoned. */
export const STALE_AFTER_MIN = 120;

export type FocusPhase = "running" | "overrun" | "ended";

type Timed = Pick<FocusSession, "planned_min" | "started_at" | "ended_at">;

export function elapsedMs(s: Timed, now: Date): number {
	const end = s.ended_at ? Date.parse(s.ended_at) : now.getTime();
	return Math.max(0, end - Date.parse(s.started_at));
}

/** Time left on the plan; negative once the plan is exceeded. */
export function remainingMs(s: Timed, now: Date): number {
	return s.planned_min * MIN - elapsedMs(s, now);
}

export function phase(s: Timed, now: Date): FocusPhase {
	if (s.ended_at) return "ended";
	return remainingMs(s, now) >= 0 ? "running" : "overrun";
}

/** Only a still-running session can go stale. */
export function isStale(s: Timed, now: Date): boolean {
	return !s.ended_at && elapsedMs(s, now) > (s.planned_min + STALE_AFTER_MIN) * MIN;
}

/** `24:59`, `1:05:00`; overrun shows `+0:30`. Rounds the remaining time up so 0:00 means done. */
export function formatClock(ms: number): string {
	const over = ms < 0;
	const total = Math.ceil(Math.abs(ms) / 1000);
	const h = Math.floor(total / 3600);
	const m = Math.floor((total % 3600) / 60);
	const s = total % 60;
	const body = h
		? `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
		: `${m}:${String(s).padStart(2, "0")}`;
	return over ? `+${body}` : body;
}

/**
 * What finishing does. Reaching the plan completes the session; stopping
 * before it is "ended early" and does not count as completed. A stale
 * session is closed at its planned end, since the owner left long ago.
 */
export function finishPatch(s: Timed, now: Date): Pick<FocusSession, "ended_at" | "completed"> {
	const planned = s.planned_min * MIN;
	if (isStale(s, now)) {
		return {
			ended_at: new Date(Date.parse(s.started_at) + planned).toISOString(),
			completed: false,
		};
	}
	return { ended_at: now.toISOString(), completed: elapsedMs(s, now) >= planned };
}

/** The plan after "+5 min", never above the cap. */
export function extendedPlan(s: Pick<FocusSession, "planned_min">): number {
	return Math.min(MAX_PLANNED_MIN, s.planned_min + EXTEND_MIN);
}

/** Whole minutes actually focused in a finished session (rounded, at least 1 once started). */
export function focusedMinutes(s: Timed): number {
	if (!s.ended_at) return 0;
	return Math.max(1, Math.round(elapsedMs(s, new Date(s.ended_at)) / MIN));
}

/** Total minutes across finished sessions. */
export function totalFocusedMinutes(sessions: Timed[]): number {
	return sessions.reduce((sum, s) => sum + focusedMinutes(s), 0);
}

/** The length offered for a task: its estimate rounded to 5 minutes (5 to 120), else 25. */
export function defaultFocusMinutes(task: { estimate_min: number | null }): number {
	const est = task.estimate_min;
	if (!est) return 25;
	return Math.min(120, Math.max(5, Math.round(est / 5) * 5));
}
